import React, { useState, useEffect } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Input } from '@voilajsx/uikit/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import {
  Shield, ChevronLeft, ChevronRight, Filter, Clock, Eye, X,
  UserCircle, CreditCard, Download, Settings, Package, FileText,
  ArrowRight, Copy, Check
} from 'lucide-react';
import { AdminSidebar } from '../components/AdminSidebar';
import { Header, SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { route } from '../../../shared/utils';
import { config } from '../../auth/config';

interface AuditEntry {
  id: string;
  createdAt: string;
  actorId: number;
  actorName: string;
  action: string;
  entityType: string;
  entityId: string;
  description: string;
  ipAddress?: string;
  oldValues?: Record<string, any> | null;
  newValues?: Record<string, any> | null;
}

const GOLD = '#FFD93D';

const getEntityIcon = (entityType: string) => {
  switch (entityType?.toLowerCase()) {
    case 'user': return UserCircle;
    case 'credit': return CreditCard;
    case 'download': return Download;
    case 'setting': return Settings;
    case 'service': return Package;
    case 'payment': return CreditCard;
    default: return FileText;
  }
};

const getActionStyle = (action: string) => {
  if (action.includes('delete') || action.includes('remove') || action.includes('revoke'))
    return { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', border: 'rgba(239,68,68,0.3)' };
  if (action.includes('create') || action.includes('grant') || action.includes('signup'))
    return { bg: 'rgba(34,197,94,0.15)', color: '#22c55e', border: 'rgba(34,197,94,0.3)' };
  if (action.includes('update') || action.includes('edit') || action.includes('status'))
    return { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', border: 'rgba(59,130,246,0.3)' };
  if (action.includes('download') || action.includes('confirm'))
    return { bg: 'rgba(255,217,61,0.15)', color: GOLD, border: 'rgba(255,217,61,0.3)' };
  return { bg: 'rgba(161,161,170,0.15)', color: '#a1a1aa', border: 'rgba(161,161,170,0.3)' };
};

const timeAgo = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fullDateTime = (dateStr: string): string =>
  new Date(dateStr).toLocaleString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

/* ─── Detail Popup ──────────────────────────────────────────── */
const AuditDetailPopup: React.FC<{ entry: AuditEntry; onClose: () => void }> = ({ entry, onClose }) => {
  const actionStyle = getActionStyle(entry.action);
  const Icon = getEntityIcon(entry.entityType);
  const [copied, setCopied] = useState(false);

  const copyId = () => {
    navigator.clipboard.writeText(entry.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #222' }}>
      <span style={{ width: 110, flexShrink: 0, fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em', paddingTop: 1 }}>
        {label}
      </span>
      <div style={{ flex: 1, fontSize: 14, color: '#ccc', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 999,
          backdropFilter: 'blur(4px)', animation: 'fadeIn 0.15s ease'
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 540,
        background: '#111', borderLeft: '1px solid #2a2a2a', zIndex: 1000,
        display: 'flex', flexDirection: 'column', animation: 'slideIn 0.2s ease',
        overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #222',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: '#111', zIndex: 1
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: actionStyle.bg, border: `1px solid ${actionStyle.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Icon style={{ width: 20, height: 20, color: actionStyle.color }} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>Audit Log Detail</h3>
              <span style={{ fontSize: 12, color: '#555' }}>{timeAgo(entry.createdAt)}</span>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: '#1a1a1a', border: '1px solid #333', borderRadius: 8,
            width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#888'
          }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 24px', flex: 1 }}>
          {/* Action badge */}
          <div style={{ marginBottom: 20 }}>
            <span style={{
              fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 6,
              background: actionStyle.bg, color: actionStyle.color, border: `1px solid ${actionStyle.border}`,
              textTransform: 'uppercase', letterSpacing: '0.04em'
            }}>
              {entry.action.replace(/_/g, ' ')}
            </span>
          </div>

          {/* Info rows */}
          <div>
            <InfoRow label="Event ID">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <code style={{ fontSize: 12, color: '#888', background: '#1a1a1a', padding: '2px 6px', borderRadius: 4 }}>
                  {entry.id}
                </code>
                <button onClick={copyId} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#555'
                }}>
                  {copied ? <Check style={{ width: 14, height: 14, color: '#22c55e' }} /> : <Copy style={{ width: 14, height: 14 }} />}
                </button>
              </div>
            </InfoRow>

            <InfoRow label="Timestamp">{fullDateTime(entry.createdAt)}</InfoRow>

            <InfoRow label="Actor">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', background: '#222',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: GOLD
                }}>
                  {(entry.actorName || '?')[0].toUpperCase()}
                </div>
                <span>{entry.actorName || `User #${entry.actorId}`}</span>
                <code style={{ fontSize: 11, color: '#555' }}>ID: {entry.actorId}</code>
              </div>
            </InfoRow>

            <InfoRow label="Entity">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 12, background: '#1a1a1a', padding: '2px 8px', borderRadius: 4,
                  color: '#999', fontWeight: 500, border: '1px solid #222'
                }}>
                  {entry.entityType}
                </span>
                {entry.entityId && (
                  <code style={{ fontSize: 12, color: '#666' }}>#{entry.entityId}</code>
                )}
              </div>
            </InfoRow>

            <InfoRow label="Description">
              <span style={{ color: '#aaa' }}>{entry.description}</span>
            </InfoRow>

            {entry.ipAddress && (
              <InfoRow label="IP Address">
                <code style={{ fontSize: 13, color: '#888' }}>{entry.ipAddress}</code>
              </InfoRow>
            )}
          </div>

          {/* Changes section */}
          {(entry.oldValues || entry.newValues) && (
            <div style={{ marginTop: 24 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#888', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Changes
              </h4>

              {entry.oldValues && entry.newValues ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 600, color: '#ef4444', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Before
                    </p>
                    <pre style={{
                      fontSize: 12, color: '#999', background: '#0d0d0d', padding: 14,
                      borderRadius: 8, border: '1px solid #1a1a1a', overflow: 'auto',
                      maxHeight: 280, margin: 0, fontFamily: 'monospace', lineHeight: 1.6
                    }}>
{JSON.stringify(entry.oldValues, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 600, color: '#22c55e', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      After
                    </p>
                    <pre style={{
                      fontSize: 12, color: '#999', background: '#0d0d0d', padding: 14,
                      borderRadius: 8, border: '1px solid #1a1a1a', overflow: 'auto',
                      maxHeight: 280, margin: 0, fontFamily: 'monospace', lineHeight: 1.6
                    }}>
{JSON.stringify(entry.newValues, null, 2)}
                    </pre>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{
                    fontSize: 11, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em',
                    color: entry.newValues ? '#22c55e' : '#ef4444'
                  }}>
                    {entry.newValues ? 'New Values' : 'Previous Values'}
                  </p>
                  <pre style={{
                    fontSize: 12, color: '#999', background: '#0d0d0d', padding: 14,
                    borderRadius: 8, border: '1px solid #1a1a1a', overflow: 'auto',
                    maxHeight: 300, margin: 0, fontFamily: 'monospace', lineHeight: 1.6
                  }}>
{JSON.stringify(entry.newValues || entry.oldValues, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideIn { from { transform: translateX(100%) } to { transform: translateX(0) } }
      `}</style>
    </>
  );
};

/* ─── Main Page ─────────────────────────────────────────────── */
const AdminAuditPage: React.FC = () => {
  const { user, token } = useAuth();

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 30;

  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);

  // User filter
  const [users, setUsers] = useState<{ id: number; name: string | null; email: string }[]>([]);
  const [filterActorId, setFilterActorId] = useState('');
  const [filterUserSearch, setFilterUserSearch] = useState('');

  const isAdminSystem = user?.role === 'admin' && user?.level === 'system';
  const isModeratorManage = user?.role === 'moderator' && user?.level === 'manage';
  const hasAccess = isAdminSystem || isModeratorManage;

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  });

  const fetchAuditLogs = async (pageNum: number = page) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(pageNum),
        pageSize: String(pageSize),
      });
      if (actionFilter !== 'all') params.set('action', actionFilter);
      if (entityFilter !== 'all') params.set('entityType', entityFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (filterActorId) params.set('actorId', filterActorId);

      const res = await fetch(`${config.api.baseUrl}/api/audit/admin/list?${params}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      const logs = data.data?.entries || data.data?.logs || data.entries || data.logs || data.data || [];
      setEntries(logs);
      setHasMore(logs.length === pageSize);
      if (data.data?.total) setTotalCount(data.data.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch audit logs');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${config.api.baseUrl}/api/user/admin/list?page=1&pageSize=200`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setUsers(data.data?.users || data.users || data.data || []);
    } catch { /* non-critical */ }
  };

  useEffect(() => {
    if (hasAccess && token) {
      fetchAuditLogs(1);
      fetchUsers();
    }
  }, [token, hasAccess]);

  if (!user) return null;

  const applyFilters = () => {
    setPage(1);
    fetchAuditLogs(1);
  };

  const clearFilters = () => {
    setActionFilter('all');
    setEntityFilter('all');
    setDateFrom('');
    setDateTo('');
    setFilterActorId('');
    setFilterUserSearch('');
    setPage(1);
    setTimeout(() => fetchAuditLogs(1), 0);
  };

  const goToPage = (newPage: number) => {
    if (newPage < 1) return;
    setPage(newPage);
    fetchAuditLogs(newPage);
  };

  const hasActiveFilters = actionFilter !== 'all' || entityFilter !== 'all' || dateFrom || dateTo || filterActorId;

  if (!hasAccess) {
    return (
      <PageLayout size="full">
        <SEO title="Access Denied" description="You don't have permission to access this page" />
        <Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-destructive" />
                  <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                  <p className="text-muted-foreground mb-4">You don't have permission to view audit logs.</p>
                  <Button asChild><a href={route('/dashboard')}>Back to Dashboard</a></Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  // Group entries by date
  const groupedEntries: { date: string; items: AuditEntry[] }[] = [];
  entries.forEach((entry) => {
    const dateKey = new Date(entry.createdAt).toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    const existing = groupedEntries.find((g) => g.date === dateKey);
    if (existing) existing.items.push(entry);
    else groupedEntries.push({ date: dateKey, items: [entry] });
  });

  return (
    <PageLayout size="full">
      <SEO title="Audit Logs" description="System audit trail" />
      <Header />

      <PageLayout.Content>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <AdminSidebar />
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 72 }}>
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold tracking-tight">Audit Logs</h1>
                  <p className="text-muted-foreground">
                    System activity trail
                    {totalCount > 0 && <span className="ml-1">· {totalCount.toLocaleString()} total events</span>}
                  </p>
                </div>
                <Button
                  variant={showFilters ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-2"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {hasActiveFilters && (
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%', background: GOLD, color: '#111',
                      fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      !
                    </span>
                  )}
                </Button>
              </div>

              {error && (
                <Alert className="bg-destructive/10 border-destructive text-destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Filters Panel */}
              {showFilters && (
                <div style={{
                  background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12,
                  padding: 20, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end'
                }}>
                  <div style={{ flex: '1 1 200px', minWidth: 180, position: 'relative' }}>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">User</label>
                    <div style={{ position: 'relative' }}>
                      <Input
                        placeholder="Search user..."
                        value={filterUserSearch}
                        onChange={(e) => {
                          setFilterUserSearch(e.target.value);
                          if (!e.target.value) setFilterActorId('');
                        }}
                      />
                      {filterActorId && (
                        <button
                          onClick={() => { setFilterActorId(''); setFilterUserSearch(''); }}
                          style={{
                            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: 2
                          }}
                        >
                          <X style={{ width: 14, height: 14 }} />
                        </button>
                      )}
                    </div>
                    {filterUserSearch && !filterActorId && (
                      <div style={{
                        border: '1px solid #333', borderRadius: 8, background: '#111',
                        maxHeight: 140, overflowY: 'auto', marginTop: 4, position: 'absolute', zIndex: 10,
                        width: '100%', left: 0,
                      }}>
                        {users
                          .filter((u) => `${u.name || ''} ${u.email}`.toLowerCase().includes(filterUserSearch.toLowerCase()))
                          .slice(0, 6)
                          .map((u) => (
                            <div
                              key={u.id}
                              onClick={() => {
                                setFilterActorId(String(u.id));
                                setFilterUserSearch(u.name || u.email);
                              }}
                              style={{
                                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                                borderBottom: '1px solid #1a1a1a',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#1a1a1a'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              <div style={{ fontWeight: 600, color: '#ddd' }}>{u.name || '(no name)'}</div>
                              <div style={{ fontSize: 11, color: '#888' }}>{u.email}</div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">Action</label>
                    <Select value={actionFilter} onValueChange={setActionFilter}>
                      <SelectTrigger><SelectValue placeholder="All Actions" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Actions</SelectItem>
                        <SelectItem value="create">Create</SelectItem>
                        <SelectItem value="update">Update</SelectItem>
                        <SelectItem value="delete">Delete</SelectItem>
                        <SelectItem value="login">Login</SelectItem>
                        <SelectItem value="status_change">Status Change</SelectItem>
                        <SelectItem value="signup_bonus_granted">Signup Bonus</SelectItem>
                        <SelectItem value="download_token_issued">Download</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">Entity</label>
                    <Select value={entityFilter} onValueChange={setEntityFilter}>
                      <SelectTrigger><SelectValue placeholder="All Entities" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Entities</SelectItem>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="service">Service</SelectItem>
                        <SelectItem value="credit">Credit</SelectItem>
                        <SelectItem value="payment">Payment</SelectItem>
                        <SelectItem value="setting">Setting</SelectItem>
                        <SelectItem value="download">Download</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div style={{ flex: '1 1 140px', minWidth: 130 }}>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">From</label>
                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  </div>
                  <div style={{ flex: '1 1 140px', minWidth: 130 }}>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">To</label>
                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button onClick={applyFilters} size="sm" style={{ background: GOLD, color: '#111' }}>Apply</Button>
                    {hasActiveFilters && (
                      <Button onClick={clearFilters} variant="ghost" size="sm">Clear</Button>
                    )}
                  </div>
                </div>
              )}

              {/* Timeline */}
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} style={{
                      background: '#1a1a1a', borderRadius: 10, padding: '16px 20px',
                      display: 'flex', gap: 16, alignItems: 'center'
                    }}>
                      <div className="h-10 w-10 rounded-full bg-zinc-800 animate-pulse shrink-0" />
                      <div style={{ flex: 1 }}>
                        <div className="h-4 w-3/4 bg-zinc-800 rounded animate-pulse mb-2" />
                        <div className="h-3 w-1/2 bg-zinc-800 rounded animate-pulse" />
                      </div>
                      <div className="h-3 w-16 bg-zinc-800 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : entries.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '64px 20px', background: '#1a1a1a',
                  borderRadius: 12, border: '1px solid #2a2a2a'
                }}>
                  <Shield style={{ width: 48, height: 48, margin: '0 auto 16px', color: '#555' }} />
                  <p style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 4 }}>No audit entries found</p>
                  <p style={{ color: '#666', fontSize: 14 }}>
                    {hasActiveFilters ? 'Try adjusting your filters' : 'Activity will appear here as actions are performed'}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {groupedEntries.map((group) => (
                    <div key={group.date}>
                      {/* Date header */}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12
                      }}>
                        <Clock style={{ width: 14, height: 14, color: '#666' }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {group.date}
                        </span>
                        <div style={{ flex: 1, height: 1, background: '#2a2a2a' }} />
                        <span style={{ fontSize: 12, color: '#555' }}>{group.items.length} events</span>
                      </div>

                      {/* Entries */}
                      <div className="space-y-2">
                        {group.items.map((entry) => {
                          const Icon = getEntityIcon(entry.entityType);
                          const actionStyle = getActionStyle(entry.action);

                          return (
                            <div
                              key={entry.id}
                              style={{
                                background: '#1a1a1a',
                                border: '1px solid #222',
                                borderRadius: 10,
                                padding: '14px 18px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                                transition: 'border-color 0.15s',
                              }}
                            >
                              {/* Entity icon */}
                              <div style={{
                                width: 38, height: 38, borderRadius: '50%',
                                background: actionStyle.bg, border: `1px solid ${actionStyle.border}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                              }}>
                                <Icon style={{ width: 18, height: 18, color: actionStyle.color }} />
                              </div>

                              {/* Content */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: 14, fontWeight: 600, color: '#e5e5e5' }}>
                                    {entry.actorName || `User #${entry.actorId}`}
                                  </span>
                                  <span style={{
                                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                                    background: actionStyle.bg, color: actionStyle.color, border: `1px solid ${actionStyle.border}`,
                                    textTransform: 'uppercase', letterSpacing: '0.03em'
                                  }}>
                                    {entry.action.replace(/_/g, ' ')}
                                  </span>
                                  <span style={{ fontSize: 12, color: '#666' }}>on</span>
                                  <span style={{
                                    fontSize: 12, color: '#999', fontWeight: 500,
                                    background: '#222', padding: '1px 6px', borderRadius: 4
                                  }}>
                                    {entry.entityType}
                                  </span>
                                  {entry.entityId && (
                                    <span style={{ fontSize: 11, color: '#555', fontFamily: 'monospace' }}>
                                      #{entry.entityId}
                                    </span>
                                  )}
                                </div>
                                <p style={{
                                  fontSize: 13, color: '#777', marginTop: 3,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                }}>
                                  {entry.description}
                                </p>
                              </div>

                              {/* Time + View button */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                                <span style={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>
                                  {timeAgo(entry.createdAt)}
                                </span>
                                <button
                                  onClick={() => setSelectedEntry(entry)}
                                  style={{
                                    background: '#222', border: '1px solid #333', borderRadius: 6,
                                    padding: '5px 10px', cursor: 'pointer', color: '#999',
                                    display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500,
                                    transition: 'all 0.15s',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#2a2a2a';
                                    e.currentTarget.style.color = GOLD;
                                    e.currentTarget.style.borderColor = GOLD;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#222';
                                    e.currentTarget.style.color = '#999';
                                    e.currentTarget.style.borderColor = '#333';
                                  }}
                                >
                                  <Eye style={{ width: 14, height: 14 }} />
                                  View
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {!isLoading && entries.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 0'
                }}>
                  <span style={{ fontSize: 13, color: '#666' }}>
                    Page {page}{totalCount > 0 && ` of ${Math.ceil(totalCount / pageSize)}`}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="outline" size="sm" onClick={() => goToPage(page - 1)} disabled={page === 1} className="gap-1">
                      <ChevronLeft className="h-4 w-4" /> Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => goToPage(page + 1)} disabled={!hasMore} className="gap-1">
                      Next <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </PageLayout.Content>

      {/* Detail Popup */}
      {selectedEntry && (
        <AuditDetailPopup entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
      )}
    </PageLayout>
  );
};

export default AdminAuditPage;
